import React, { createContext, useState } from 'react'
import { SmartControlTestRecord } from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { ModalFormId } from '../../ModalForm'
import { DateRange } from '../../../../pages/AuditBot/AuditBot.hooks'
import {
  AuditScanModalContextType,
  ButtonConfig,
  ControlDisplay,
  DisplayConfig,
} from './AuditScanModal.constants'

/**
 * Context for Audit Bot functionalities.
 *
 * @typedef {Object} AuditScanModalContextType
 *
 * @property {string} auditId - the relevant id for the control test result
 * @property {Function} setAuditId - Setter function for auditId.
 *
 * @property {string} title - Title of the modal.
 * @property {Function} setTitle - Setter function for title.
 *
 * @property {string} description - Description of the modal.
 * @property {Function} setDescription - Setter function for description.
 *
 * @property {Function | null} onBackModalId - Modal to navigate to when the back button is clicked. If null, the back button will not be displayed.
 * @property {Function} setOnBackModalId - Setter function for onBackModalId.
 *
 * @property {Array<ControlDisplay>} controlsFilter - List of ControlDisplay options to filter controls displayed in the modal. NOTE: This is an OR filter, not an AND filter.
 * @property {Function} setControlsFilter - Setter function for controlsFilter.
 *
 * @property {Array<ControlDisplay>} controlsPreselect - List of ControlDisplay options to preselect controls in the modal.  NOTE: This is an OR filter, not an AND filter.
 * @property {Function} setControlsPreselect - Setter function for controlsPreselect.
 *
 * @property {Array<Control>} filteredControls - List of controls to be displayed based on the filter.
 * @property {Function} setFilteredControls - Setter function for filteredControls.
 *
 * @property {Array<string>} preselectedControlIds - List of preselected control IDs in the modal.
 * @property {Function} setPreselectedControlIds - Setter function for preselectedControlIds.
 *
 * @property {Object<string, SmartControlTestRecord>} controlTestMap - Map storing the last run control test for each control. If a control ID is not present, the test has not been run against it.
 * @property {Function} setControlTestMap - Setter function for controlTestMap.
 *
 * @property {Object} displayConfig - Configuration for display options and previous test results visibility.
 * @property {Function} setDisplayConfig - Setter function for displayConfig.
 *
 * @property {Object} singleControlId - Single control id.
 * @property {Function} setSingleControlId - Setter function for single control id.
 */
export const AuditScanModalContext = createContext<AuditScanModalContextType>({
  auditId: '',
  setAuditId: () => null,
  title: '',
  setTitle: () => null,
  description: '',
  setDescription: () => null,
  onBackModalId: null,
  setOnBackModalId: () => null,
  controlsFilter: [ControlDisplay.ALL],
  setControlsFilter: () => null,
  controlsPreselect: [ControlDisplay.ALL],
  setControlsPreselect: () => null,
  filteredControls: [],
  setFilteredControls: () => null,
  preselectedControlIds: [],
  setPreselectedControlIds: () => null,
  controlTestMap: {},
  setControlTestMap: () => null,
  displayConfig: {
    buttonConfig: ButtonConfig.ALL,
    showResults: true,
  },
  setDisplayConfig: () => null,
  singleControlId: undefined,
  setSingleControlId: () => undefined,
  dateRange: {
    startDate: new Date(),
    endDate: new Date(),
  },
  setDateRange: () => null,
  dateLimit: new Date(),
  setDateLimit: () => null,
})

export const AuditScanModalContextProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  const [auditId, setAuditId] = useState<string>('')
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: new Date(),
    endDate: new Date(),
  })

  const [title, setTitle] = useState<string>('')
  const [description, setDescription] = useState<string | JSX.Element>('')
  const [onBackModalId, setOnBackModalId] = useState<null | ModalFormId>(null)
  const [controlsFilter, setControlsFilter] = useState<ControlDisplay[]>([
    ControlDisplay.ALL,
  ])
  const [controlsPreselect, setControlsPreselect] = useState<ControlDisplay[]>([
    ControlDisplay.ALL,
  ])
  const [filteredControls, setFilteredControls] = useState<Control[]>([])
  const [preselectedControlIds, setPreselectedControlIds] = useState<string[]>(
    [],
  )
  const [controlTestMap, setControlTestMap] = useState<
    Record<string, SmartControlTestRecord>
  >({})
  const [displayConfig, setDisplayConfig] = useState<DisplayConfig>({
    buttonConfig: ButtonConfig.ALL,
    showResults: true,
  })
  const [singleControlId, setSingleControlId] = useState<string | undefined>()
  const [dateLimit, setDateLimit] = useState<Date>(new Date())

  return (
    <AuditScanModalContext.Provider
      value={{
        auditId,
        setAuditId,
        title,
        setTitle,
        description,
        setDescription,
        onBackModalId,
        setOnBackModalId,
        controlsFilter,
        setControlsFilter,
        controlsPreselect,
        setControlsPreselect,
        filteredControls,
        setFilteredControls,
        preselectedControlIds,
        setPreselectedControlIds,
        controlTestMap,
        setControlTestMap,
        displayConfig,
        setDisplayConfig,
        singleControlId,
        setSingleControlId,
        dateRange,
        setDateRange,
        dateLimit,
        setDateLimit,
      }}
    >
      {children}
    </AuditScanModalContext.Provider>
  )
}
